import { View, Text, styled } from 'tamagui'
import { useState } from 'react'
import { useAppStore, useServerStore } from '@/store'

const Overlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '$overlay',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
})

const Modal = styled(View, {
  width: 480,
  maxHeight: '80vh',
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  overflow: 'hidden',
})

const Header = styled(View, {
  alignItems: 'center',
  gap: 12,
  paddingTop: 32,
  paddingBottom: 20,
  paddingHorizontal: 32,
})

const ServerIcon = styled(View, {
  width: 64,
  height: 64,
  borderRadius: '$borderRadius2xl',
  alignItems: 'center',
  justifyContent: 'center',
})

const ServerInitials = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '700',
  fontSize: 24,
  color: '$surface',
})

const ServerName = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '700',
  fontSize: 22,
  color: '$surfaceForeground',
})

const Description = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$sidebarMuted',
  textAlign: 'center',
  lineHeight: 20,
})

const Divider = styled(View, {
  height: 1,
  backgroundColor: '$borderDefault',
  marginHorizontal: 32,
})

const RulesContainer = styled(View, {
  paddingHorizontal: 32,
  paddingVertical: 20,
  gap: 12,
})

const RulesTitle = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const RuleRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 12,
  cursor: 'pointer',
})

const Checkbox = styled(View, {
  width: 20,
  height: 20,
  borderRadius: '$borderRadiusSm',
  borderWidth: 2,
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 1,
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, border-color 150ms ease' },
})

const CheckMark = styled(Text, {
  fontSize: 12,
  fontWeight: '700',
  color: '$surface',
  lineHeight: 14,
})

const RuleNumber = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  fontWeight: '600',
  color: '$color8',
  width: 20,
  flexShrink: 0,
})

const RuleText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$surfaceForeground',
  lineHeight: 20,
  flex: 1,
})

const Footer = styled(View, {
  paddingHorizontal: 32,
  paddingBottom: 24,
  paddingTop: 8,
})

const AgreeButton = styled(View, {
  height: 40,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, opacity 150ms ease' },
})

const AgreeText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  fontWeight: '600',
  color: '$surface',
})

export function ServerWelcomeScreen() {
  const { welcomeScreenServerId, setWelcomeScreenServerId } = useAppStore()
  const server = useServerStore((s) => welcomeScreenServerId ? s.servers[welcomeScreenServerId] : undefined)
  const [checkedRules, setCheckedRules] = useState<Set<number>>(new Set())

  if (!server || !server.welcomeScreen) return null

  const { description, rules } = server.welcomeScreen
  const allChecked = checkedRules.size === rules.length

  const toggleRule = (index: number) => {
    setCheckedRules((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  return (
    <Overlay onPress={() => setWelcomeScreenServerId(null)}>
      <Modal onPress={(e: { stopPropagation: () => void }) => e.stopPropagation()}>
        <Header>
          <ServerIcon backgroundColor={server.iconColor}>
            <ServerInitials>{server.iconText}</ServerInitials>
          </ServerIcon>
          <ServerName>{server.name}</ServerName>
          <Description>{description}</Description>
        </Header>

        <Divider />

        <RulesContainer>
          <RulesTitle>Server Rules</RulesTitle>
          {rules.map((rule, i) => {
            const checked = checkedRules.has(i)
            return (
              <RuleRow key={i} onPress={() => toggleRule(i)}>
                <Checkbox
                  borderColor={checked ? '$accent' : '$color8'}
                  backgroundColor={checked ? '$accent' : 'transparent'}
                >
                  {checked && <CheckMark>✓</CheckMark>}
                </Checkbox>
                <RuleNumber>{i + 1}.</RuleNumber>
                <RuleText>{rule}</RuleText>
              </RuleRow>
            )
          })}
        </RulesContainer>

        <Footer>
          <AgreeButton
            backgroundColor={allChecked ? '$accent' : '$sidebarActive'}
            opacity={allChecked ? 1 : 0.5}
            onPress={() => {
              if (allChecked) setWelcomeScreenServerId(null)
            }}
          >
            <AgreeText color={allChecked ? '$surface' : '$sidebarMuted'}>
              I have read and agree to the rules
            </AgreeText>
          </AgreeButton>
        </Footer>
      </Modal>
    </Overlay>
  )
}
